import type {
  BranchScaleConfig,
  LiveScaleWeightResult,
  ScaleAgentHealthResult,
} from '../domain/scaleConfig';
import { isAllowedLocalAgentUrl } from '../../shared/localAgentUrl';

const SCALE_HEALTH_TIMEOUT_MS = 4000;
const SCALE_WEIGHT_TIMEOUT_MS = 3000;

/**
 * Timeout propio + respeta el signal del caller. `AbortSignal.any` no está en
 * todos los runtimes (Hermes) — fallback manual cuando falta.
 */
function fetchSignalWithTimeout(signal: AbortSignal | undefined, timeoutMs: number): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(timeoutMs);
  if (!signal) return timeoutSignal;
  if (typeof AbortSignal.any === 'function') {
    return AbortSignal.any([signal, timeoutSignal]);
  }
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal.aborted || timeoutSignal.aborted) {
    abort();
    return controller.signal;
  }
  signal.addEventListener('abort', abort, { once: true });
  timeoutSignal.addEventListener('abort', abort, { once: true });
  return controller.signal;
}

// #795/#1294: misma allowlist que printing — el agente de balanza es el mismo
// proceso (`vuelto-print-agent` :9310) y puede vivir en otra PC de la LAN.
function resolveAgentBase(config: Pick<BranchScaleConfig, 'agentUrl'>): string | null {
  const defaultBase = 'http://127.0.0.1:9310';
  const base = config.agentUrl.replace(/\/$/, '') || defaultBase;
  if (!isAllowedLocalAgentUrl(base)) return null;
  return base;
}

/** Ping del agente local (GET /health). */
export async function fetchScaleAgentHealth(
  config: Pick<BranchScaleConfig, 'agentUrl'>,
  signal?: AbortSignal,
): Promise<ScaleAgentHealthResult> {
  const base = resolveAgentBase(config);
  if (!base) {
    return {
      ok: false,
      error: 'URL del agente no permitida. Solo hosts locales o de red local.',
    };
  }
  try {
    const res = await fetch(`${base}/health`, {
      method: 'GET',
      signal: fetchSignalWithTimeout(signal, SCALE_HEALTH_TIMEOUT_MS),
    });
    const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (!res.ok) {
      return {
        ok: false,
        error: typeof data?.message === 'string' ? data.message : `Agente no disponible (${res.status}).`,
      };
    }
    return {
      ok: true,
      service: typeof data?.service === 'string' ? data.service : 'vuelto-print-agent',
      port: typeof data?.port === 'number' ? data.port : 9310,
      scaleDefaultProtocol:
        typeof data?.scale_default_protocol === 'string' ? data.scale_default_protocol : undefined,
      scalePortEnv: typeof data?.scale_port === 'string' ? data.scale_port : null,
      serialportAvailable: data?.serialport_available === true,
    };
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      return { ok: false, error: 'cancelado' };
    }
    return {
      ok: false,
      error: 'No se pudo hablar con el agente (¿está corriendo en :9310?).',
    };
  }
}

export async function fetchLiveScaleWeight(
  config: Pick<BranchScaleConfig, 'agentUrl' | 'protocol' | 'comPort'>,
  signal?: AbortSignal,
): Promise<LiveScaleWeightResult> {
  const base = resolveAgentBase(config);
  if (!base) {
    return {
      ok: false,
      error: 'URL del agente no permitida. Solo hosts locales o de red local.',
    };
  }
  const qs = new URLSearchParams({ protocol: config.protocol });
  if (config.comPort.trim()) qs.set('port', config.comPort.trim());
  const url = `${base}/scale/weight?${qs.toString()}`;
  try {
    const res = await fetch(url, {
      method: 'GET',
      signal: fetchSignalWithTimeout(signal, SCALE_WEIGHT_TIMEOUT_MS),
    });
    const data = (await res.json().catch(() => null)) as Record<string, unknown> | null;
    if (!res.ok) {
      const message =
        typeof data?.message === 'string'
          ? data.message
          : `Agente balanza no disponible (${res.status}).`;
      return { ok: false, error: message };
    }
    const grams = typeof data?.grams === 'number' ? data.grams : null;
    if (grams == null || !Number.isFinite(grams) || grams < 0) {
      return {
        ok: false,
        error: typeof data?.message === 'string' ? data.message : 'Esperando peso…',
      };
    }
    return {
      ok: true,
      reading: {
        grams,
        kg: grams / 1000,
        stable: data?.stable === true,
        source: typeof data?.source === 'string' ? data.source : config.protocol,
        waiting: data?.waiting === true,
        message: typeof data?.message === 'string' ? data.message : undefined,
      },
    };
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') {
      return { ok: false, error: 'cancelado' };
    }
    return {
      ok: false,
      error: 'No se pudo hablar con el agente (¿está corriendo en :9310?).',
    };
  }
}
